import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import {
  LayoutDashboard,
  Users,
  FileText,
  UserCog,
  LogOut,
  Briefcase,
  CalendarOff,
  Wallet,
  CalendarClock,
  TrendingUp,
  CarFront,
  Clock,
  CalendarX,
  Stethoscope,
  Gavel,
  LogIn,
  BellRing,
  ClipboardCheck,
  CalendarDays,
  Fuel,
  SprayCan,
  Wallet as WalletIcon,
  PieChart,
  Receipt,
  ChevronDown,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import DateTimeWeather from "@/components/DateTimeWeather";
import AgendaNotificationsBell from "@/components/AgendaNotificationsBell";

// Certaines entrées sont des groupes (menu unique avec sous-menus) plutôt que
// des liens directs : "Clients" + "Affectations clients" notamment. Le menu
// "Contrats" a été retiré : la création de contrats et d'avenants se fait
// désormais directement depuis la fiche de chaque salarié. Le menu "Sites
// clients" a également été retiré du sous-menu "Clients".
const NAV = [
  { to: "/dashboard", label: "Tableau de bord", icon: LayoutDashboard, roles: ["admin_rh", "manager"] },
  { to: "/employees", label: "Salariés", icon: Users, roles: ["admin_rh", "manager"] },
  { to: "/hr-documents", label: "Documents & RH", icon: FileText, roles: ["admin_rh", "manager"] },
  {
    label: "Clients", icon: Briefcase, roles: ["admin_rh", "manager"],
    children: [
      { to: "/clients", label: "Clients" },
      { to: "/assignments", label: "Affectations clients" },
      { to: "/clients-lost", label: "Pertes chantiers" },
    ],
  },
  {
    label: "Absences & congés", icon: CalendarOff, roles: ["admin_rh", "manager"],
    children: [
      { to: "/absences", label: "Absences & congés" },
      { to: "/replacements", label: "Remplacements" },
    ],
  },
  { to: "/planning", label: "Annexes des agents", icon: CalendarClock, roles: ["admin_rh", "manager"] },
  { to: "/planning-semaine", label: "Planning", icon: CalendarDays, roles: ["admin_rh", "manager"] },
  { to: "/agenda", label: "Agenda", icon: CalendarDays, roles: ["admin_rh", "manager"] },
  { to: "/closures", label: "Fermetures clients", icon: CalendarX, roles: ["admin_rh", "manager"] },
  { to: "/hours", label: "Heures & majorations", icon: Clock, roles: ["admin_rh"] },
  { to: "/payroll", label: "Variables de paie", icon: Wallet, roles: ["admin_rh"] },
  { to: "/medical", label: "Médecine du travail", icon: Stethoscope, roles: ["admin_rh", "manager"] },
  { to: "/disciplinary", label: "Disciplinaire", icon: Gavel, roles: ["admin_rh"] },
  { to: "/lifecycle", label: "Entrées / sorties", icon: LogIn, roles: ["admin_rh"] },
  { to: "/alerts", label: "Alertes RH", icon: BellRing, roles: ["admin_rh", "manager"], badge: "alerts" },
  { to: "/revenue", label: "Chiffre d'affaires", icon: TrendingUp, roles: ["admin_rh"] },
  { to: "/profitability", label: "Rentabilité chantier", icon: PieChart, roles: ["admin_rh"] },
  {
    label: "Charges", icon: Receipt, roles: ["admin_rh"],
    children: [
      { to: "/charges/salaires", label: "Salaires et cotisations" },
      { to: "/charges/suivi", label: "Tableau de suivi des charges" },
      { to: "/charges/tva", label: "TVA" },
    ],
  },
  { to: "/vehicles", label: "Véhicules", icon: CarFront, roles: ["admin_rh", "manager"] },
  { to: "/fuel", label: "Carburant", icon: Fuel, roles: ["admin_rh", "manager"] },
  { to: "/window-cleaning", label: "Laveur de vitres", icon: SprayCan, roles: ["admin_rh", "manager"] },
  { to: "/cleaning-tracking", label: "Prestations ponctuelles", icon: SprayCan, roles: ["admin_rh", "manager"] },
  { to: "/checklist", label: "Checklist prestations", icon: ClipboardCheck, roles: ["admin_rh", "manager"] },
  { to: "/checks", label: "Suivi des chèques", icon: WalletIcon, roles: ["admin_rh", "manager"] },
  { to: "/users", label: "Utilisateurs", icon: UserCog, roles: ["admin_rh"] },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem("sidebar-collapsed") === "1";
    } catch {
      return false;
    }
  });
  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem("sidebar-collapsed", next ? "1" : "0");
      } catch {
        // ignore
      }
      return next;
    });
  };

  const isGroupActive = (n) => n.children?.some((c) => location.pathname.startsWith(c.to));
  const [openGroups, setOpenGroups] = useState(() => {
    const initial = {};
    NAV.forEach((n) => { if (n.children) initial[n.label] = isGroupActive(n); });
    return initial;
  });
  const toggleGroup = (label) => setOpenGroups((prev) => ({ ...prev, [label]: !prev[label] }));

  // Badge "Alertes RH". Les alertes sont recalculées à la volée côté serveur
  // (pas d'état "lu"/"acquitté" stocké) : le compteur reflète donc les alertes
  // NON RÉSOLUES, et disparaît dès qu'il n'en reste plus — le simple fait
  // d'ouvrir la page ne l'efface pas. Rouge s'il existe au moins une alerte
  // critique (severity "red"), bleu sinon.
  const [alertBadge, setAlertBadge] = useState({ total: 0, urgent: false });
  useEffect(() => {
    let cancelled = false;
    const fetchAlerts = () => api.get("/hr/alerts")
      .then((r) => {
        if (cancelled) return;
        setAlertBadge({ total: r.data?.total || 0, urgent: (r.data?.by_severity?.red || 0) > 0 });
      })
      .catch(() => {});
    fetchAlerts();
    const t = setInterval(fetchAlerts, 5 * 60 * 1000);
    return () => { cancelled = true; clearInterval(t); };
  }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex bg-gray-50">
      {/* Sidebar */}
      <aside
        className={`shrink-0 bg-white border-r border-gray-200 flex flex-col sticky top-0 h-screen overflow-y-auto transition-all duration-200 ${
          collapsed ? "w-16" : "w-64"
        }`}
      >
        <div className={`py-6 border-b border-gray-200 ${collapsed ? "px-2" : "px-6"}`}>
          <div className={`flex items-center ${collapsed ? "justify-center" : "gap-3"}`}>
            {!collapsed && (
              <img src="/net-proprete-logo.png" alt="Net Propreté" className="h-20 w-32 object-contain shrink-0" />
            )}
            {!collapsed && (
              <div>
                <div className="font-display text-base font-bold tracking-tight text-gray-900">Net Propreté</div>
                <div className="text-xs text-gray-500 tracking-[0.12em] uppercase">SIRH</div>
              </div>
            )}
          </div>
        </div>

        <div className={`px-2 py-2 border-b border-gray-200 flex ${collapsed ? "justify-center" : "justify-end"}`}>
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleCollapsed}
            data-testid="sidebar-collapse-toggle"
            title={collapsed ? "Développer le menu" : "Réduire le menu"}
            aria-label={collapsed ? "Développer le menu" : "Réduire le menu"}
            className="h-8 w-8 p-0 text-gray-500 hover:text-gray-900"
          >
            {collapsed ? <ChevronsRight className="w-4 h-4" /> : <ChevronsLeft className="w-4 h-4" />}
          </Button>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1" data-testid="main-nav">
          {NAV.filter((n) => n.roles.includes(user?.role)).map((n) => (
            n.children ? (
              <div key={n.label}>
                <button
                  type="button"
                  onClick={() => !collapsed && toggleGroup(n.label)}
                  data-testid={`nav-group-${n.label.replace(/\s+/g, "-").toLowerCase()}`}
                  title={collapsed ? n.label : undefined}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                    collapsed ? "justify-center px-0" : ""
                  } ${
                    isGroupActive(n) ? "text-sky-700 font-medium" : "text-gray-700 hover:bg-gray-50 hover:text-gray-900"
                  }`}
                >
                  <n.icon className="w-4 h-4" strokeWidth={1.7} />
                  {!collapsed && <span className="flex-1 text-left">{n.label}</span>}
                  {!collapsed && (openGroups[n.label] ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />)}
                </button>
                {!collapsed && openGroups[n.label] && (
                  <div className="ml-6 border-l border-gray-200 pl-3 mt-1 space-y-1">
                    {n.children.map((c) => (
                      <NavLink
                        key={c.to}
                        to={c.to}
                        data-testid={`nav-${c.to.slice(1)}`}
                        className={({ isActive }) =>
                          `block px-3 py-1.5 rounded-md text-sm transition-colors ${
                            isActive
                              ? "bg-sky-50 text-sky-700 font-medium"
                              : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                          }`
                        }
                      >
                        {c.label}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.to.slice(1)}`}
                title={collapsed ? n.label : undefined}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                    collapsed ? "justify-center px-0" : ""
                  } ${
                    isActive
                      ? "bg-sky-50 text-sky-700 font-medium"
                      : "text-gray-700 hover:bg-gray-50 hover:text-gray-900"
                  }`
                }
              >
                <n.icon className="w-4 h-4" strokeWidth={1.7} />
                {!collapsed && <span className="flex-1">{n.label}</span>}
                {n.badge === "alerts" && alertBadge.total > 0 && (
                  <span
                    data-testid="alerts-badge"
                    className={`${collapsed ? "" : "ml-auto"} min-w-[20px] h-5 px-1.5 inline-flex items-center justify-center rounded-full text-[10px] font-bold text-white ${
                      alertBadge.urgent ? "bg-red-600" : "bg-sky-600"
                    }`}
                    title={alertBadge.urgent ? "Alertes urgentes à traiter" : "Nouvelles alertes à examiner"}
                  >
                    {alertBadge.total > 99 ? "99+" : alertBadge.total}
                  </span>
                )}
              </NavLink>
            )
          ))}
        </nav>

        <div className={`border-t border-gray-200 ${collapsed ? "p-2" : "p-4"}`}>
          {!collapsed && (
            <div className="mb-3">
              <div className="text-sm font-medium text-gray-900 truncate" data-testid="current-user-name">{user?.name}</div>
              <div className="text-xs text-gray-500 truncate">{user?.email}</div>
              <div className="mt-1 inline-block px-2 py-0.5 rounded-full bg-sky-50 text-sky-700 text-[10px] font-semibold tracking-wider uppercase">
                {user?.role === "admin_rh" ? "Admin RH" : "Manager"}
              </div>
            </div>
          )}
          <Button
            variant="outline"
            size="sm"
            className={collapsed ? "w-full justify-center p-0" : "w-full justify-start"}
            onClick={handleLogout}
            data-testid="logout-btn"
            title={collapsed ? "Déconnexion" : undefined}
          >
            <LogOut className={collapsed ? "w-4 h-4" : "w-4 h-4 mr-2"} />
            {!collapsed && "Déconnexion"}
          </Button>
        </div>
      </aside>

      <main className="flex-1 overflow-auto">
        <div className="relative">
          <DateTimeWeather />
          {/* Rappels de rendez-vous (agenda) : visibles sur toutes les pages,
              pas seulement en ouvrant l'onglet Agenda — voir AgendaNotificationsBell. */}
          <div className="absolute right-4 top-0 h-full flex items-center">
            <AgendaNotificationsBell />
          </div>
        </div>
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
